import React, { forwardRef, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useToast } from '#client/components/common/Toast/ToastContext';
import { Card, Text, Box, Button, Grid, ButtonGroup, BlockStack, Spinner, InlineStack, TextField, Modal, Combobox, Listbox, Tooltip } from '@shopify/polaris';
import { useTransferHeader } from '#client/stores/transfer/useTransferStore';
import { SessionContext } from '#client/contexts/SessionContext.jsx';
import CustomBadge from '#client/components/common/CustomBadge/CustomBadge';
import StoreLocation from '#client/components/StoreLocation';
import StoreAddress from '#client/components/StoreAddress';
import StoreLocationSelector from '#client/components/StoreLocationSelector';
import { hasPermission } from '#client/utils/helpers';
import './TransferHeader.css';

const TransferHeader = forwardRef((props, ref) => {
    const {
        displayTransferId,
        status,
        type,
        locationFromId,
        setLocationFromId,
        setLocationToId,
        locationToId,
        mode,
        handleInitiateTransfer,
        handleCancelTransfer,
        handleShipTransfer,
        lines,
        trackingNumber,
        setTrackingNumber,
        carrier,
        setCarrier,
        newTransferModalOpen,
        setNewTransferModalOpen,
        handleNewTransferOverages,
        checkTransferForExceptions,
        handlePackingSlip,
        isPackingSlipLoading,
        hasPermissionForActionOnTransfer,
        canTransfer,
    } = useTransferHeader();

    const isNavigatingRef = ref;
    const navigate = useNavigate();
    const { addToast, clearToasts } = useToast();
    const { currentUser } = useContext(SessionContext);
    const [loading] = useState(true);
    const [confirmReceiveModal, setConfirmReceiveModal] = useState(false);
    const [confirmCancelModal, setConfirmCancelModal] = useState(false);

    useEffect(() => {
        if (mode == 'create' && localStorage.getItem('activeStore') !== 'ALL' && lines.length == 0) {
            setLocationFromId(localStorage.getItem('activeStore'));
        }
    });

    const viewStatusMarkup = (
        <Box paddingBlock="400">
            <Grid>
                <Grid.Cell columnSpan={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                    <BlockStack gap="200">
                        <Text variant="headingSm" as="h3">
                            Transfer ID: {loading && !displayTransferId ? <Spinner size="small" /> : displayTransferId}
                        </Text>
                        <Text variant="headingSm" as="h3">
                            Status: <CustomBadge status={status || 'Active'} />
                            Type: {loading && !type ? <Spinner size="small" /> : type || ''}
                        </Text>
                    </BlockStack>
                </Grid.Cell>
                <Grid.Cell columnSpan={{ xs: 6, sm: 3, md: 3, lg: 6, xl: 6 }}>
                    <BlockStack gap="200">
                        <Text variant="headingSm" as="h3">
                            Shipping Store: <StoreLocation locationId={locationFromId} />
                        </Text>
                        <Text>
                            <StoreAddress locationId={locationFromId} />
                        </Text>
                    </BlockStack>
                </Grid.Cell>
                <Grid.Cell columnSpan={{ xs: 6, sm: 3, md: 3, lg: 6, xl: 6 }}>
                    <BlockStack gap="200">
                        <Text variant="headingSm" as="h3">
                            Receiving Store: <StoreLocation locationId={locationToId} />
                            {locationToId == '' && (
                                <Text>
                                    <em>No receiving store selected</em>
                                </Text>
                            )}
                        </Text>
                        <Text>
                            <StoreAddress locationId={locationToId} />
                        </Text>
                    </BlockStack>
                </Grid.Cell>
                <Grid.Cell columnSpan={{ xs: 6, sm: 3, md: 3, lg: 6, xl: 6 }}>
                    <Text variant="headingSm" as="h3">
                        Carrier:
                    </Text>
                    {carrier && trackingNumber ? (
                        <Text>
                            {carrier} - {trackingNumber}
                        </Text>
                    ) : (
                        <Text>
                            <em>Missing shipping info, cannot mark as in transit or view packing slip, please edit and fill in missing information</em>
                        </Text>
                    )}
                </Grid.Cell>
                <Grid.Cell columnSpan={{ xs: 6, sm: 3, md: 3, lg: 6, xl: 6 }}>
                    <InlineStack align="end">
                        {status !== 'Exception' && status !== 'Canceled' && (
                            <ButtonGroup>
                                {status != 'In Transit' &&
                                    status != 'Complete' &&
                                    status != 'Draft' &&
                                    hasPermission(currentUser, 'transferUpdate') &&
                                    hasPermissionForActionOnTransfer(locationFromId, currentUser.locationIds) && (
                                        <>
                                            {lines.length == 0 || carrier == '' || trackingNumber == '' ? (
                                                <Tooltip content="Missing shipping info, cannot mark as in transit">
                                                    <Button variant="primary" disabled={lines.length == 0 || carrier == '' || trackingNumber == ''}>
                                                        Mark as In Transit
                                                    </Button>
                                                </Tooltip>
                                            ) : (
                                                <Button
                                                    variant="primary"
                                                    onClick={() => {
                                                        handleShipTransfer(navigate, addToast);
                                                    }}
                                                    disabled={lines.length == 0 || carrier == '' || trackingNumber == '' || isPackingSlipLoading}
                                                >
                                                    Mark as In Transit
                                                </Button>
                                            )}
                                        </>
                                    )}
                                {(status == 'In Transit' || status == 'Initiated') && (
                                    <>
                                        {lines.length == 0 || carrier == '' || trackingNumber == '' ? (
                                            <Tooltip content="Missing shipping info, cannot view packing slip">
                                                <Button variant="primary" disabled={lines.length == 0 || carrier == '' || trackingNumber == ''}>
                                                    Print Packing Slip
                                                </Button>
                                            </Tooltip>
                                        ) : (
                                            <Button
                                                variant="primary"
                                                onClick={() => {
                                                    handlePackingSlip(addToast, clearToasts);
                                                }}
                                                loading={isPackingSlipLoading}
                                                disabled={lines.length == 0 || carrier == '' || trackingNumber == '' || isPackingSlipLoading}
                                            >
                                                Print Packing Slip
                                            </Button>
                                        )}
                                    </>
                                )}
                                {status == 'Draft' && hasPermissionForActionOnTransfer(locationFromId, currentUser.locationIds) && (
                                    <Button
                                        variant="primary"
                                        onClick={() => {
                                            handleInitiateTransfer('Initiated', navigate, addToast);
                                        }}
                                        disabled={lines.length == 0 || locationToId == '' || !canTransfer()}
                                    >
                                        Initiate Transfer
                                    </Button>
                                )}
                                {mode == 'receive' && status !== 'Complete' && (
                                    <Button variant="primary" onClick={() => setConfirmReceiveModal(true)} disabled={lines.length == 0}>
                                        Mark Transfer as Received
                                    </Button>
                                )}
                                <Modal
                                    open={confirmReceiveModal}
                                    onClose={() => setConfirmReceiveModal(false)}
                                    title="Confirm Transfer Received"
                                    primaryAction={{
                                        content: 'Yes, Mark as Received',
                                        onAction: () => {
                                            setConfirmReceiveModal(false);
                                            checkTransferForExceptions(navigate, addToast);
                                        },
                                    }}
                                    secondaryActions={[
                                        {
                                            content: 'Cancel',
                                            onAction: () => setConfirmReceiveModal(false),
                                        },
                                    ]}
                                >
                                    <Modal.Section>
                                        <BlockStack gap="300">
                                            <Text as="p">Are you sure you want to mark this transfer as Received?</Text>
                                            {lines.length > 0 && lines.every((line) => Number(line.qtyReceived ?? 0) === 0) && (
                                                <Text as="p" tone="critical">
                                                    WARNING: You are about to receive a transfer with 0 units on all lines. Please ensure this is an intended usecase.
                                                </Text>
                                            )}
                                        </BlockStack>
                                    </Modal.Section>
                                </Modal>
                            </ButtonGroup>
                        )}
                    </InlineStack>
                </Grid.Cell>
            </Grid>
        </Box>
    );

    const editCreateStatusMarkup = (
        <Box className="transfer-form-grid" paddingBlock="400">
            <Grid>
                {mode != 'create' && (
                    <Grid.Cell columnSpan={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                        <BlockStack gap="200">
                            <Text variant="headingSm" as="h3">
                                Transfer ID: {loading && !displayTransferId ? <Spinner size="small" /> : displayTransferId}
                            </Text>
                        </BlockStack>
                    </Grid.Cell>
                )}
                <Grid.Cell columnSpan={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                    <BlockStack gap="200">
                        <Text variant="headingSm" as="h3">
                            Status: <CustomBadge status={status || 'Active'} />
                            Type: {loading && !type ? <Spinner size="small" /> : type || ''}
                        </Text>
                    </BlockStack>
                </Grid.Cell>
                <Grid.Cell columnSpan={{ xs: 6, sm: 3, md: 3, lg: 6, xl: 6 }}>
                    {localStorage.getItem('activeStore') == 'ALL' && mode == 'create' && lines.length == 0 ? (
                        <BlockStack gap="200">
                            <Text variant="headingSm" as="h3">
                                Shipping Store:
                            </Text>
                            <StoreLocationSelector locationId={locationFromId} excludeLocationId={[locationToId]} setLocationId={setLocationFromId} />
                        </BlockStack>
                    ) : (
                        <BlockStack gap="200">
                            <Text variant="headingSm" as="h3">
                                Shipping Store: <StoreLocation locationId={locationFromId} />
                            </Text>
                            <StoreAddress locationId={locationFromId} />
                        </BlockStack>
                    )}
                </Grid.Cell>
                <Grid.Cell columnSpan={{ xs: 6, sm: 3, md: 3, lg: 6, xl: 6 }}></Grid.Cell>
                <Grid.Cell columnSpan={{ xs: 6, sm: 3, md: 3, lg: 6, xl: 6 }}>
                    {status == 'Draft' && (mode == 'edit' || mode == 'create') ? (
                        <BlockStack gap="200">
                            <Text variant="headingSm" as="h3">
                                Receiving Store:
                            </Text>
                            <StoreLocationSelector locationId={locationToId} excludeLocationId={[locationFromId]} setLocationId={setLocationToId} onlyHasAccess={false} />
                        </BlockStack>
                    ) : (
                        <BlockStack gap="200">
                            <Text variant="headingSm" as="h3">
                                Receiving Store: <StoreLocation locationId={locationToId} />
                            </Text>
                            <StoreAddress locationId={locationToId} />
                        </BlockStack>
                    )}
                </Grid.Cell>
                <Grid.Cell columnSpan={{ xs: 6, sm: 3, md: 3, lg: 6, xl: 6 }}></Grid.Cell>
                <Grid.Cell columnSpan={{ xs: 6, sm: 3, md: 3, lg: 3, xl: 3 }}>
                    <BlockStack gap="200">
                        <Text variant="headingSm" as="h3">
                            Shipping Carrier:
                        </Text>
                        <Combobox
                            activator={
                                <Combobox.TextField
                                    onChange={setCarrier}
                                    label="Select Shipping Carrier"
                                    labelHidden
                                    value={carrier}
                                    placeholder="Select Shipping Carrier"
                                    autoComplete="off"
                                    readOnly
                                    clearButton={Boolean(carrier)}
                                    onClearButtonClick={() => setCarrier('')}
                                />
                            }
                        >
                            <Listbox onSelect={setCarrier}>
                                <Listbox.Option key="UPS" value="UPS" selected={carrier === 'UPS'} accessibilityLabel="UPS">
                                    UPS
                                </Listbox.Option>
                                <Listbox.Option key="FedEx" value="FedEx" selected={carrier === 'FedEx'} accessibilityLabel="FedEx">
                                    FedEx
                                </Listbox.Option>
                                <Listbox.Option key="USPS" value="USPS" selected={carrier === 'USPS'} accessibilityLabel="USPS">
                                    USPS
                                </Listbox.Option>
                                <Listbox.Option key="DHL" value="DHL" selected={carrier === 'DHL'} accessibilityLabel="DHL">
                                    DHL
                                </Listbox.Option>
                                <Listbox.Option key="Hand Carry" value="Hand Carry" selected={carrier === 'Hand Carry'} accessibilityLabel="Hand Carry">
                                    Hand Carry
                                </Listbox.Option>
                                <Listbox.Option key="Other" value="Other" selected={carrier === 'Other'} accessibilityLabel="Other">
                                    Other
                                </Listbox.Option>
                            </Listbox>
                        </Combobox>
                    </BlockStack>
                </Grid.Cell>
                <Grid.Cell columnSpan={{ xs: 6, sm: 3, md: 3, lg: 3, xl: 3 }}>
                    <BlockStack gap="200">
                        {carrier == '' || carrier != 'Hand Carry' ? (
                            <Text variant="headingSm" as="h3">
                                Shipping Tracking Number(s):
                            </Text>
                        ) : (
                            <Text variant="headingSm" as="h3">
                                Notes:
                            </Text>
                        )}
                        <TextField value={trackingNumber} onChange={setTrackingNumber} autoComplete="off" />
                    </BlockStack>
                </Grid.Cell>
                <Grid.Cell columnSpan={{ xs: 6, sm: 3, md: 3, lg: 6, xl: 6 }}></Grid.Cell>
                <Grid.Cell columnSpan={{ xs: 6, sm: 3, md: 3, lg: 6, xl: 6 }}></Grid.Cell>
                <Grid.Cell columnSpan={{ xs: 6, sm: 3, md: 3, lg: 6, xl: 6 }}>
                    <InlineStack align="end">
                        <ButtonGroup>
                            {mode == 'edit' && status !== 'Complete' && status !== 'Exception' && status !== 'Canceled' && (
                                <Button
                                    variant="primary"
                                    tone="critical"
                                    onClick={() => {
                                        setConfirmCancelModal(true);
                                    }}
                                >
                                    Cancel Transfer
                                </Button>
                            )}
                            <Modal
                                open={confirmCancelModal}
                                onClose={() => setConfirmCancelModal(false)}
                                title="Confirm Transfer Cancellation"
                                primaryAction={{
                                    content: 'Yes, Cancel Transfer',
                                    onAction: () => {
                                        setConfirmCancelModal(false);
                                        handleCancelTransfer(navigate, addToast);
                                    },
                                    destructive: true,
                                }}
                                secondaryActions={[
                                    {
                                        content: 'Cancel',
                                        onAction: () => setConfirmCancelModal(false),
                                    },
                                ]}
                            >
                                <Modal.Section>
                                    <Text as="p">Are you sure you want to cancel this transfer?</Text>
                                </Modal.Section>
                            </Modal>
                            {status == 'Draft' && (
                                <Button
                                    onClick={() => {
                                        isNavigatingRef.current = true;
                                        handleInitiateTransfer('Draft', navigate, addToast);
                                    }}
                                    disabled={lines.length == 0 || locationToId == ''}
                                >
                                    Save as Draft
                                </Button>
                            )}
                            <Button
                                variant="primary"
                                onClick={() => {
                                    isNavigatingRef.current = true;
                                    handleInitiateTransfer('Initiated', navigate, addToast);
                                }}
                                disabled={lines.length == 0 || locationToId == ''}
                            >
                                {mode == 'create' || status == 'Draft' ? 'Initiate' : 'Save'} Transfer
                            </Button>
                        </ButtonGroup>
                    </InlineStack>
                </Grid.Cell>
            </Grid>
        </Box>
    );

    return (
        <>
            <Card>
                <Box paddingBlock="400">
                    <Text variant="headingLg" as="h2">
                        Transfer Details
                    </Text>
                </Box>
                {mode === 'view' || mode == 'receive' ? viewStatusMarkup : editCreateStatusMarkup}
            </Card>
            <Modal
                open={newTransferModalOpen}
                onClose={() => {
                    setNewTransferModalOpen(false);
                }}
                title="Transfer Item Overage"
                primaryAction={{
                    content: 'Create New Transfer',
                    onAction: () => {
                        handleNewTransferOverages(navigate, addToast);
                    },
                    destructive: true,
                }}
                secondaryActions={[
                    {
                        content: 'Send Items Back',
                        onAction: () => {
                            setNewTransferModalOpen(false);
                        },
                    },
                ]}
            >
                <Modal.Section>
                    <Text as="p">You have received items which were not on the transfer, would you like to create a new transfer from the store into your store for these items?</Text>
                </Modal.Section>
            </Modal>
        </>
    );
});

TransferHeader.displayName = 'TransferHeader';
export default TransferHeader;
